"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createPersistedQueryManifestVerificationLink = exports.generatePersistedQueryIdsFromManifest = exports.sortTopLevelDefinitions = exports.generatePersistedQueryIdsAtRuntime = void 0;
const graphql_1 = require("graphql");
const core_1 = require("@apollo/client/link/core");
const core_2 = require("@apollo/client/core");
function generatePersistedQueryIdsAtRuntime(options) {
    return {
        generateHash: (query) => Promise.resolve(options.sha256((0, graphql_1.print)(sortTopLevelDefinitions(query)))),
        disable: () => false,
    };
}
exports.generatePersistedQueryIdsAtRuntime = generatePersistedQueryIdsAtRuntime;
function sortTopLevelDefinitions(query) {
    const definitions = [...query.definitions];
    definitions.sort((a, b) => {
        if (a.kind > b.kind) {
            return -1;
        }
        if (a.kind < b.kind) {
            return 1;
        }
        const aName = a.kind === "OperationDefinition" || a.kind === "FragmentDefinition"
            ? a.name?.value ?? ""
            : "";
        const bName = b.kind === "OperationDefinition" || b.kind === "FragmentDefinition"
            ? b.name?.value ?? ""
            : "";
        if (aName < bName) {
            return -1;
        }
        if (aName > bName) {
            return 1;
        }
        return 0;
    });
    return {
        ...query,
        definitions,
    };
}
exports.sortTopLevelDefinitions = sortTopLevelDefinitions;
function generatePersistedQueryIdsFromManifest(options) {
    const operationIdsByNamePromise = Promise.resolve(options.loadManifest()).then((manifest) => {
        const operationIdsByName = new Map();
        manifest.operations.forEach(({ name, id }) => {
            operationIdsByName.set(name, id);
        });
        return operationIdsByName;
    });
    operationIdsByNamePromise.catch(() => { });
    async function generateHash(document) {
        const operationIdsByName = await operationIdsByNamePromise;
        let operationName = null;
        for (const definition of document.definitions) {
            if (definition.kind === "OperationDefinition") {
                if (!definition.name) {
                    throw new Error("Anonymous operations are not supported by generatePersistedQueryIdsFromManifest");
                }
                if (operationName !== null) {
                    throw new Error("Multi-operation GraphQL documents are not supported by generatePersistedQueryIdsFromManifest");
                }
                operationName = definition.name.value;
            }
        }
        if (operationName === null) {
            throw new Error("Documents without operations are not supported by generatePersistedQueryIdsFromManifest");
        }
        const operationId = operationIdsByName.get(operationName);
        if (operationId === undefined) {
            throw new Error(`Operation ${operationName} not found in manifest`);
        }
        return operationId;
    }
    return {
        generateHash,
        disable: () => false,
    };
}
exports.generatePersistedQueryIdsFromManifest = generatePersistedQueryIdsFromManifest;
function createPersistedQueryManifestVerificationLink(options) {
    const operationsByNamePromise = Promise.resolve(options.loadManifest()).then((manifest) => {
        const operationsByName = new Map();
        manifest.operations.forEach((operation) => {
            operationsByName.set(operation.name, operation);
        });
        return operationsByName;
    });
    operationsByNamePromise.catch(() => { });
    function verifyOperation(operation, operationsByName) {
        const query = (0, graphql_1.print)(sortTopLevelDefinitions(operation.query));
        let operationName = null;
        for (const definition of operation.query.definitions) {
            if (definition.kind === "OperationDefinition") {
                if (!definition.name) {
                    options.onVerificationFailed?.({
                        reason: "AnonymousOperation",
                        operation,
                    });
                    return;
                }
                if (operationName !== null) {
                    options.onVerificationFailed?.({
                        reason: "MultipleOperations",
                        operation,
                    });
                    return;
                }
                operationName = definition.name.value;
            }
        }
        if (operationName === null) {
            options.onVerificationFailed?.({ reason: "NoOperations", operation });
            return;
        }
        const manifestOperation = operationsByName.get(operationName);
        if (manifestOperation === undefined) {
            options.onVerificationFailed?.({
                reason: "UnknownOperation",
                operation,
            });
            return;
        }
        if (query !== manifestOperation.body) {
            options.onVerificationFailed?.({
                reason: "OperationMismatch",
                operation,
                manifestOperation,
            });
            return;
        }
    }
    return new core_1.ApolloLink((operation, forward) => {
        return new core_2.Observable((observer) => {
            let handle;
            let closed = false;
            operationsByNamePromise
                .then((operationsByName) => {
                verifyOperation(operation, operationsByName);
                if (closed)
                    return;
                handle = forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer),
                });
            })
                .catch(observer.error.bind(observer));
            return () => {
                closed = true;
                if (handle)
                    handle.unsubscribe();
            };
        });
    });
}
exports.createPersistedQueryManifestVerificationLink = createPersistedQueryManifestVerificationLink;
//# sourceMappingURL=index.js.map